import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  Ban,
  UserX,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  AlertTriangle,
  Globe,
  Clock,
} from "lucide-react";
import { UserData } from "../../../lib/types/authOnboarding";
import { useGetDecryptedSSN } from "../../../lib/api/adminUsers";

interface UserProfileCardProps {
  user: UserData;
  formatDate: (dateString?: string) => string;
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  formatDate,
}) => {
  const [showSSN, setShowSSN] = useState(false);
  const [decryptedSSN, setDecryptedSSN] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { mutate: fetchSSN, isPending: isLoadingSSN } = useGetDecryptedSSN();

  const handleRevealSSN = () => {
    setShowConfirmModal(true);
  };

  const confirmRevealSSN = () => {
    setShowConfirmModal(false);

    fetchSSN(user._id, {
      onSuccess: (data) => {
        setDecryptedSSN(data.ssn);
        setShowSSN(true);

        // Auto-hide after 30 seconds for security
        setTimeout(() => {
          setShowSSN(false);
          setDecryptedSSN(null);
        }, 30000);
      },
    });
  };

  const handleHideSSN = () => {
    setShowSSN(false);
    setDecryptedSSN(null);
  };

  const formatSSN = (ssn: string): string => {
    if (ssn.length === 9) {
      return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
    }
    return ssn;
  };

  const hasSSN = user.verification?.ssnLastFour;

  // Check if user has application location data
  const hasApplicationLocation =
    user.applicationCity ||
    user.applicationRegion ||
    user.applicationGeoLocation ||
    user.applicationIp;

  // Format application location string
  const getApplicationLocationString = () => {
    const parts = [];
    if (user.applicationCity) parts.push(user.applicationCity);
    if (user.applicationRegion) parts.push(user.applicationRegion);
    if (user.applicationGeoLocation) parts.push(user.applicationGeoLocation);
    return parts.join(", ") || "Unknown location";
  };

  // Confirmation Modal Component - Rendered via Portal
  const ConfirmationModal = () => {
    if (!showConfirmModal) return null;

    return createPortal(
      <div className="fixed inset-0 z-[99999] flex items-center justify-center">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowConfirmModal(false)}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 z-10">
          <div className="text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Reveal Sensitive Information
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              You are about to view the full Social Security Number for{" "}
              <span className="font-medium">
                {user.firstname} {user.lastname}
              </span>
              . This action will be logged for security purposes.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevealSSN}
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Yes, Reveal SSN
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        {/* Avatar & Name */}
        <div className="text-center mb-6">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white text-3xl font-bold mb-4">
            {user.firstname?.[0]}
            {user.lastname?.[0]}
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            {user.firstname} {user.lastname}
          </h2>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mt-2 ${
              user.status === "active"
                ? "bg-emerald-50 text-emerald-700"
                : user.status === "suspended"
                  ? "bg-orange-50 text-orange-700"
                  : user.status === "terminated"
                    ? "bg-red-50 text-red-700"
                    : "bg-gray-100 text-gray-600"
            }`}
          >
            {user.status === "active" && (
              <CheckCircle className="w-3.5 h-3.5" />
            )}
            {user.status === "suspended" && <Ban className="w-3.5 h-3.5" />}
            {user.status === "terminated" && <UserX className="w-3.5 h-3.5" />}
            {user.status?.charAt(0).toUpperCase() + user.status?.slice(1)}
          </span>
        </div>

        {/* Contact Info */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-gray-600">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-sm">{user.email}</span>
          </div>
          {user.phoneNumber && (
            <div className="flex items-center gap-3 text-gray-600">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{user.phoneNumber}</span>
            </div>
          )}
          {user.address && (
            <div className="flex items-center gap-3 text-gray-600">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm">
                {user.address.city}, {user.address.country}
              </span>
            </div>
          )}
          <div className="flex items-center gap-3 text-gray-600">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm">Joined {formatDate(user.createdAt)}</span>
          </div>
        </div>

        {/* Application Location */}
        {hasApplicationLocation && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Application Location
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2.5">
              {(user.applicationCity ||
                user.applicationRegion ||
                user.applicationGeoLocation) && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="text-sm text-gray-900">
                      {getApplicationLocationString()}
                    </p>
                  </div>
                </div>
              )}
              {user.applicationTimezone && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Timezone</p>
                    <p className="text-sm text-gray-900">
                      {user.applicationTimezone}
                    </p>
                  </div>
                </div>
              )}
              {user.applicationIp && (
                <div className="flex items-start gap-3">
                  <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">IP Address</p>
                    <p className="text-sm text-gray-900 font-mono">
                      {user.applicationIp}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verification Status */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Verification Status
          </h3>
          <div className="space-y-2">
            {[
              {
                label: "Email",
                verified: user.verification?.emailVerified,
              },
              {
                label: "Address",
                verified: user.verification?.addressVerified,
              },
              {
                label: "SSN",
                verified: user.verification?.ssnVerified,
                hasData: hasSSN,
              },
              {
                label: "Document",
                verified: user.verification?.documentVerified,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between"
              >
                <span className="text-sm text-gray-600">{item.label}</span>
                {item.verified ? (
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-gray-300" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* SSN Section */}
        {hasSSN && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Social Security Number
            </h3>

            <div className="bg-gray-50 rounded-lg p-4">
              {showSSN && decryptedSSN ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Full SSN</p>
                      <p className="text-lg font-mono font-semibold text-gray-900">
                        {formatSSN(decryptedSSN)}
                      </p>
                    </div>
                    <button
                      onClick={handleHideSSN}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Hide SSN"
                    >
                      <EyeOff className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Auto-hides in 30 seconds
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">SSN (Last 4)</p>
                    <p className="text-lg font-mono font-semibold text-gray-900">
                      •••-••-{user.verification?.ssnLastFour || "••••"}
                    </p>
                  </div>
                  <button
                    onClick={handleRevealSSN}
                    disabled={isLoadingSSN}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoadingSSN ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Reveal SSN
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {user.verification?.ssnSubmittedAt && (
              <p className="text-xs text-gray-500 mt-2">
                Submitted on {formatDate(user.verification.ssnSubmittedAt)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Render Modal via Portal */}
      <ConfirmationModal />
    </>
  );
};

export default UserProfileCard;
